import {
  apiUtil,
  useProjectId,
  useLazyGetMeetingTranscriptsQuery,
  useLazyGetMeetingSummaryQuery,
  useCreateMeetingSummaryMutation,
  useUpdateMeetingSummaryMutation,
  useLazyGetMeetingActionItemsQuery,
  useGenerateMeetingActionItemsMutation,
  useLazyGetMeetingAgreementsQuery,
  useGenerateMeetingAgreementsMutation,
  useLazyGetMeetingAgreementContradictionsQuery,
  useGenerateMeetingAgreementContradictionsMutation,
  useLazyGetDocumentContradictionsQuery,
  useGenerateDocumentContradictionsMutation,
  useGetPackagesQuery,
  useGetProjectParticipantsQuery,
  useAssignSegmentSpeakerMutation,
} from '@consigli/hooks';
import {
  isRetriableTaskErrorCode,
  SUMMARY_SEGMENTS_NOT_READY,
  taskErrorBodySchema,
  TaskErrorCode,
  type Transcript,
  type MeetingSummary as MeetingSummaryType,
  type ActionItem,
  type MeetingAgreement,
  type AgreementContradiction as AgreementContradictionType,
  type DocumentContradiction as DocumentContradictionType,
  type DocumentPackage,
  type ProjectParticipant,
  type SpeakerAssignScope,
} from '@consigli/types';
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';

import { useMsalAuth } from './use-msal-auth';
import { getBackendErrorCode, getNumericStatus } from '../utils/mutation-error';
import { TRANSCRIPT_MESSAGE_KEYS } from '../utils/transcript-quality';
import { parseTranscriptSegments, sortTranscriptsByCreation } from '../utils/transcript-utils';

import type { Meeting, ParsedTranscriptEntry } from '../utils/export-types';

const EMPTY_PACKAGES: DocumentPackage[] = [];
const EMPTY_PARTICIPANTS: ProjectParticipant[] = [];

const HTTP_STATUS = {
  BAD_REQUEST: 400,
  NOT_FOUND: 404,
  PRECONDITION_FAILED: 412,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
} as const;

const TASK_ERROR_FALLBACK_MESSAGES: Record<string, string> = {
  [TaskErrorCode.DOCUMENT_PACKAGE_PROCESSING]:
    'Document package is still processing. Please try again in a few minutes.',
  [TaskErrorCode.DOCUMENT_PACKAGE_EMPTY]:
    'This document package has no documents to analyse. Upload documents and try again.',
};

const readTaskError = (err: unknown) => {
  const body = taskErrorBodySchema.safeParse((err as { data?: unknown })?.data);
  if (!body.success) return null;

  const code = body.data.error_code;
  if (!code) return null;

  return {
    code,
    message:
      body.data.user_friendly_message ||
      TASK_ERROR_FALLBACK_MESSAGES[code] ||
      body.data.detail ||
      'Document contradictions could not be generated.',
    retriable: body.data.retriable ?? isRetriableTaskErrorCode(code),
  };
};

// Codes the backend gives when this user's token cannot reach the meeting in Teams.
const TEAMS_UNREACHABLE_CODES = new Set([
  'meeting_not_found',
  'transcript_access_error',
  'authentication_error',
]);

// A failed fetch the meeting is out of reach for this user (typically a client's tenant, or an
// expired meeting), so only an upload can help. Anything else (a failed task, a 5xx, no token)
// may pass, and shows the error with a way to try again.
const isTeamsUnreachable = (err: unknown): boolean => {
  const status = getNumericStatus(err);
  const code = getBackendErrorCode(err);
  return status === 401 || status === 404 || (code !== null && TEAMS_UNREACHABLE_CODES.has(code));
};

export interface BuildParamsResult {
  meetingId: string;
  projectId: number;
  msAccessToken: string;
  userEmail: string;
  userId: string;
}

export interface FetchTranscriptsOptions {
  /** Called with the fetched transcripts right before they are committed to state. */
  beforeCommit?: (fetched: Transcript[]) => void;
}

export interface UseMeetingSummaryDataResult {
  transcripts: Transcript[];
  parsedTranscripts: ParsedTranscriptEntry[];
  actionItems: ActionItem[];
  agreements: MeetingAgreement[];
  agreementContradictions: AgreementContradictionType[];
  agreementContradictionsHasFetched: boolean;
  documentContradictions: DocumentContradictionType[];
  documentContradictionsHasFetched: boolean;
  documentContradictionsRetriable: boolean;
  summary: MeetingSummaryType | null;
  error: string | null;
  errorType: 'processing' | 'legacy' | 'general' | null;
  transcriptInfoMessage: string | null;
  /** True for the organizer while the segments are text only and audio could replace them. */
  canRegenerateWithAudio: boolean;
  /** True when the backend found that no audio can replace the text, for anyone on the meeting. */
  audioUnavailable: boolean;
  summaryErrorType: typeof SUMMARY_SEGMENTS_NOT_READY | null;
  actionItemsError: string | null;
  selectedDocumentPackageId: string | null;
  projectPackages: DocumentPackage[];
  participants: ProjectParticipant[];
  assignSpeaker: (
    entry: ParsedTranscriptEntry,
    meetingUserId: string,
    scope: SpeakerAssignScope,
  ) => Promise<void>;
  isTranscriptLoading: boolean;
  isSummaryLoading: boolean;
  isSummaryGenerating: boolean;
  isAgreementsLoading: boolean;
  isAgreementsGenerating: boolean;
  isLoadingActionItems: boolean;
  isGeneratingActionItems: boolean;
  isLoadingContradictions: boolean;
  isGeneratingContradictions: boolean;
  isLoadingDocumentContradictions: boolean;
  isGeneratingDocumentContradictions: boolean;
  setSummary: Dispatch<SetStateAction<MeetingSummaryType | null>>;
  setActionItems: Dispatch<SetStateAction<ActionItem[]>>;
  setAgreements: Dispatch<SetStateAction<MeetingAgreement[]>>;
  setAgreementContradictions: Dispatch<SetStateAction<AgreementContradictionType[]>>;
  setDocumentContradictions: Dispatch<SetStateAction<DocumentContradictionType[]>>;
  setError: Dispatch<SetStateAction<string | null>>;
  buildParams: (msToken?: string) => Promise<BuildParamsResult>;
  fetchTranscripts: (options?: FetchTranscriptsOptions) => Promise<void>;
  /** Shows transcripts obtained outside `fetchTranscripts`, such as an uploaded file. */
  applyTranscripts: (fetched: Transcript[], audioUnavailable?: boolean) => void;
  /** Drops everything generated from the previous segments so it is generated again. */
  resetGeneratedData: () => void;
  fetchSummary: () => Promise<MeetingSummaryType | null>;
  saveSummary: (body: {
    summaryText?: string;
    keyDiscussionPointsText?: string;
  }) => Promise<MeetingSummaryType>;
  fetchActionItems: (signal?: AbortSignal) => Promise<ActionItem[]>;
  fetchAgreements: (signal?: AbortSignal) => Promise<MeetingAgreement[]>;
  fetchAgreementContradictions: (signal?: AbortSignal) => Promise<AgreementContradictionType[]>;
  fetchDocumentContradictions: (
    packageId?: string,
    signal?: AbortSignal,
  ) => Promise<DocumentContradictionType[]>;
  handleDocumentPackageChange: (packageId: string) => void;
  fetchAllData: () => Promise<{
    summary: MeetingSummaryType | null;
    actionItems: ActionItem[];
    agreements: MeetingAgreement[];
  }>;
}

export const useMeetingSummaryData = (meeting: Meeting): UseMeetingSummaryDataResult => {
  const [error, setError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<'processing' | 'legacy' | 'general' | null>(null);
  const [transcriptInfoMessage, setTranscriptInfoMessage] = useState<string | null>(null);
  const [canRegenerateWithAudio, setCanRegenerateWithAudio] = useState(false);
  const [audioUnavailable, setAudioUnavailable] = useState(false);
  const [summaryErrorType, setSummaryErrorType] = useState<
    typeof SUMMARY_SEGMENTS_NOT_READY | null
  >(null);
  const [actionItemsError, setActionItemsError] = useState<string | null>(null);
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [parsedTranscripts, setParsedTranscripts] = useState<ParsedTranscriptEntry[]>([]);
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [agreements, setAgreements] = useState<MeetingAgreement[]>([]);
  const [isRefetchingActionItems, setIsRefetchingActionItems] = useState(false);
  const [isRefetchingAgreements, setIsRefetchingAgreements] = useState(false);
  const renameVersionRef = useRef(0);
  const resetVersionRef = useRef(0);
  // Set once the generated data was dropped, so action items are generated again even when
  // hand-added ones remain (an empty list is otherwise the only trigger).
  const needsGenerationRef = useRef(false);
  const fetchSeqRef = useRef(0);
  const [agreementContradictions, setAgreementContradictions] = useState<
    AgreementContradictionType[]
  >([]);
  const [agreementContradictionsHasFetched, setAgreementContradictionsHasFetched] = useState(false);
  const [documentContradictions, setDocumentContradictions] = useState<DocumentContradictionType[]>(
    [],
  );
  const [documentContradictionsHasFetched, setDocumentContradictionsHasFetched] = useState(false);
  const [documentContradictionsRetriable, setDocumentContradictionsRetriable] = useState(true);
  const [summary, setSummary] = useState<MeetingSummaryType | null>(null);
  const [selectedDocumentPackageId, setSelectedDocumentPackageId] = useState<string | null>(null);

  const projectId = useProjectId();
  const dispatch = useDispatch();
  const hasRestoredDocumentPackageRef = useRef(false);
  const documentPackageStorageKey = projectId
    ? `meeting-secretary-document-package-${projectId}`
    : null;

  const { data: projectPackages = EMPTY_PACKAGES } = useGetPackagesQuery({ projectId });

  useEffect(() => {
    if (
      !documentPackageStorageKey ||
      projectPackages.length === 0 ||
      hasRestoredDocumentPackageRef.current
    )
      return;
    try {
      const stored = localStorage.getItem(documentPackageStorageKey);
      if (stored && projectPackages.some((p) => p.id === stored)) {
        setSelectedDocumentPackageId(stored);
      }
      hasRestoredDocumentPackageRef.current = true;
    } catch {
      hasRestoredDocumentPackageRef.current = true;
    }
  }, [documentPackageStorageKey, projectPackages]);

  useEffect(() => {
    hasRestoredDocumentPackageRef.current = false;
  }, [projectId]);

  const { acquireToken, userEmail, userId } = useMsalAuth();

  const [getTranscripts, { isLoading: isLoadingTranscripts, isFetching: isFetchingTranscripts }] =
    useLazyGetMeetingTranscriptsQuery();
  const [getSummary, { isLoading: isLoadingSummary }] = useLazyGetMeetingSummaryQuery();
  const [createSummary, { isLoading: isCreatingSummary }] = useCreateMeetingSummaryMutation();
  const [updateSummary] = useUpdateMeetingSummaryMutation();
  const [getActionItems, { isLoading: isLoadingActionItems }] = useLazyGetMeetingActionItemsQuery();
  const [generateActionItems, { isLoading: isGeneratingActionItems }] =
    useGenerateMeetingActionItemsMutation();
  const [getAgreements, { isLoading: isLoadingAgreements }] = useLazyGetMeetingAgreementsQuery();
  const [generateAgreements, { isLoading: isGeneratingAgreements }] =
    useGenerateMeetingAgreementsMutation();
  const [getContradictions, { isLoading: isLoadingContradictions }] =
    useLazyGetMeetingAgreementContradictionsQuery();
  const [generateContradictions, { isLoading: isGeneratingContradictions }] =
    useGenerateMeetingAgreementContradictionsMutation();
  const [
    getDocumentContradictions,
    { isLoading: isLoadingDocumentContradictions, isFetching: isFetchingDocumentContradictions },
  ] = useLazyGetDocumentContradictionsQuery();
  const [generateDocumentContradictions, { isLoading: isGeneratingDocumentContradictions }] =
    useGenerateDocumentContradictionsMutation();

  const { data: participants = EMPTY_PARTICIPANTS } = useGetProjectParticipantsQuery(
    { projectId },
    { skip: !projectId },
  );
  const [assignSegmentSpeaker] = useAssignSegmentSpeakerMutation();

  const buildParams = useCallback(
    async (msToken?: string): Promise<BuildParamsResult> => {
      const token = msToken || (await acquireToken());
      if (!token) {
        throw new Error('Failed to acquire MS token');
      }
      return { meetingId: meeting.id, projectId, msAccessToken: token, userEmail, userId };
    },
    [meeting.id, projectId, userEmail, userId, acquireToken],
  );

  const processTranscripts = useCallback((transcriptsData: Transcript[]) => {
    const sorted = sortTranscriptsByCreation(transcriptsData);

    setTranscripts(sorted);

    if (sorted.length > 0) {
      setParsedTranscripts(parseTranscriptSegments(sorted));
    }
  }, []);

  const fetchTranscripts = useCallback(
    async (options: FetchTranscriptsOptions = {}) => {
      const { beforeCommit } = options;
      // A newer call supersedes this one, so a slow earlier result cannot overwrite it.
      const seq = ++fetchSeqRef.current;
      const isStale = () => seq !== fetchSeqRef.current;

      setError(null);
      setErrorType(null);
      setTranscriptInfoMessage(null);

      // Set by the branches below and applied once in the catch. Reading `errorType`
      // from the closure here would see the value from before this call and
      // overwrite 'processing'/'legacy' with 'general'.
      let failureType: 'processing' | 'legacy' | 'general' = 'general';

      try {
        if (!meeting.joinUrl) {
          throw new Error('Meeting does not have a joinWebUrl - transcript access not available');
        }

        const request = (params: BuildParamsResult) =>
          getTranscripts({
            ...params,
            joinUrl: meeting.joinUrl,
            calendarEventId: meeting.calendarEventId,
          }).unwrap();

        let result: Awaited<ReturnType<typeof request>>;
        try {
          result = await request(await buildParams());
        } catch (err) {
          if (getNumericStatus(err) !== 401) throw err;
          // Graph refused the look-up, or the Teams sign-in expired. A renewed token tells which.
          const renewed = await acquireToken({ forceRefresh: true });
          if (!renewed) throw err;
          result = await request(await buildParams(renewed));
        }
        if (isStale()) return;

        if (result.error) {
          if (result.error === 'transcripts_processing') {
            failureType = 'processing';
            throw new Error(
              result.userFriendlyMessage ||
                'Transcripts are still being processed. Please check again in a few minutes.',
            );
          } else if (result.error === 'legacy_meeting') {
            failureType = 'legacy';
            throw new Error(
              result.userFriendlyMessage ||
                'Transcript access is not available for this meeting. New meetings created through the app will support transcripts.',
            );
          } else {
            throw new Error(result.message || 'Failed to fetch transcripts');
          }
        }

        const transcriptsWrapper = result.transcripts as {
          transcripts: Transcript[];
          messageKey?: string;
          canRegenerateWithAudio?: boolean;
          audioUnavailable?: boolean;
        };
        const fetched = transcriptsWrapper?.transcripts || [];
        const transcriptMessageKey = transcriptsWrapper?.messageKey;
        setCanRegenerateWithAudio(transcriptsWrapper?.canRegenerateWithAudio === true);
        setAudioUnavailable(transcriptsWrapper?.audioUnavailable === true);

        if (transcriptMessageKey && fetched.length === 0) {
          setTranscriptInfoMessage(transcriptMessageKey);
          return;
        }

        beforeCommit?.(fetched);
        processTranscripts(fetched);

        if (fetched.length > 0) {
          dispatch(apiUtil.invalidateTags(['Meetings']));
        }
      } catch (err) {
        if (isStale()) return;
        console.error('Error fetching transcripts:', err);

        if (isTeamsUnreachable(err)) {
          // Not an error for the user: the upload is the way forward.
          setTranscriptInfoMessage(TRANSCRIPT_MESSAGE_KEYS.uploadRequired);
          return;
        }

        const rtkMessage =
          typeof (err as { data?: unknown }).data === 'string'
            ? (err as { data: string }).data
            : null;
        setErrorType(failureType);
        setError(
          err instanceof Error ? err.message : (rtkMessage ?? 'Failed to fetch transcripts'),
        );
      }
    },
    [
      meeting.joinUrl,
      meeting.calendarEventId,
      buildParams,
      acquireToken,
      getTranscripts,
      processTranscripts,
      dispatch,
    ],
  );

  const applyTranscripts = useCallback(
    (fetched: Transcript[], unavailable = false) => {
      setError(null);
      setErrorType(null);
      setTranscriptInfoMessage(null);
      setCanRegenerateWithAudio(false);
      setAudioUnavailable(unavailable);
      processTranscripts(fetched);
    },
    [processTranscripts],
  );

  const resetGeneratedData = useCallback(() => {
    // Supersede fetches already running: they would commit what was just dropped.
    renameVersionRef.current += 1;
    resetVersionRef.current += 1;
    needsGenerationRef.current = true;
    setSummary(null);
    setSummaryErrorType(null);
    setActionItems([]);
    setActionItemsError(null);
    setAgreements([]);
    setAgreementContradictions([]);
    setAgreementContradictionsHasFetched(false);
    setDocumentContradictions([]);
    setDocumentContradictionsHasFetched(false);
    setError(null);
  }, []);

  const fetchSummary = useCallback(async () => {
    setError(null);
    setSummaryErrorType(null);

    if (transcripts.length === 0) {
      return null;
    }

    const startReset = resetVersionRef.current;
    const isReset = () => resetVersionRef.current !== startReset;

    try {
      const params = await buildParams();
      try {
        const existing = await getSummary(params).unwrap();
        if (existing) {
          if (!isReset()) setSummary(existing);
          return existing;
        }
      } catch (err: unknown) {
        const e = err as { status?: number; data?: { status?: number } };
        if (e?.status !== 404 && e?.data?.status !== 404) throw err;
      }
      const newSummary = await createSummary(params).unwrap();
      if (!isReset()) setSummary(newSummary);
      return newSummary;
    } catch (err) {
      console.error('Error with summary:', err);
      const httpErr = err as { data?: string };
      if (httpErr?.data === SUMMARY_SEGMENTS_NOT_READY) {
        setSummaryErrorType(SUMMARY_SEGMENTS_NOT_READY);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to fetch summary');
      }
      return null;
    }
  }, [transcripts.length, buildParams, getSummary, createSummary]);

  // Errors are left to the caller so a failed save stays in its editor instead
  // of replacing the whole summary with the page-level error box.
  const saveSummary = useCallback(
    async (body: { summaryText?: string; keyDiscussionPointsText?: string }) => {
      const params = await buildParams();
      const updated = await updateSummary({ ...params, body }).unwrap();
      setSummary(updated);
      return updated;
    },
    [buildParams, updateSummary],
  );

  const fetchActionItems = useCallback(
    async (signal?: AbortSignal) => {
      setActionItemsError(null);

      if (transcripts.length === 0) {
        return [];
      }

      const startVersion = renameVersionRef.current;
      const isSuperseded = () => signal?.aborted || renameVersionRef.current !== startVersion;

      setIsRefetchingActionItems(true);
      try {
        const params = await buildParams();
        const existing = await getActionItems(params).unwrap();
        // Hand-added items outlive a regenerate, so a non-empty list is not proof that the
        // generated ones exist.
        const generationPending =
          needsGenerationRef.current && !existing?.some((item) => item.sourceTranscriptId);
        if (existing?.length > 0 && !generationPending) {
          if (!isSuperseded()) setActionItems(existing);
          return existing;
        }

        if (!isSuperseded()) setIsRefetchingActionItems(false);
        const mutation = generateActionItems(params);
        signal?.addEventListener('abort', () => mutation.abort(), { once: true });
        try {
          const created = await mutation.unwrap();
          needsGenerationRef.current = false;
          // The task returns only what it created; read the list again to keep the hand-added.
          const newItems = existing?.length > 0 ? await getActionItems(params).unwrap() : created;
          if (!isSuperseded()) setActionItems(newItems);
          return newItems;
        } finally {
          signal?.removeEventListener('abort', () => mutation.abort());
        }
      } catch (err) {
        if (!isSuperseded()) {
          console.error('Error with action items:', err);
          setActionItemsError(err instanceof Error ? err.message : 'Failed to fetch action items');
        }
        return [];
      } finally {
        if (!isSuperseded()) setIsRefetchingActionItems(false);
      }
    },
    [transcripts.length, buildParams, getActionItems, generateActionItems],
  );

  const fetchAgreements = useCallback(
    async (signal?: AbortSignal) => {
      setError(null);

      if (transcripts.length === 0) {
        return [];
      }

      const startVersion = renameVersionRef.current;
      const isSuperseded = () => signal?.aborted || renameVersionRef.current !== startVersion;

      setIsRefetchingAgreements(true);
      try {
        const params = await buildParams();
        const existing = await getAgreements(params).unwrap();
        if (existing?.length > 0) {
          if (!isSuperseded()) setAgreements(existing);
          return existing;
        }

        if (!isSuperseded()) setIsRefetchingAgreements(false);
        const mutation = generateAgreements(params);
        const abortHandler = () => mutation.abort();
        signal?.addEventListener('abort', abortHandler, { once: true });
        try {
          const result = await mutation.unwrap();
          const newAgreements = result.agreements || [];
          if (!isSuperseded()) setAgreements(newAgreements);
          return newAgreements;
        } finally {
          signal?.removeEventListener('abort', abortHandler);
        }
      } catch (err) {
        if (!isSuperseded()) {
          console.error('Error with agreements:', err);
          setError(err instanceof Error ? err.message : 'Failed to fetch agreements');
        }
        return [];
      } finally {
        if (!isSuperseded()) setIsRefetchingAgreements(false);
      }
    },
    [transcripts.length, buildParams, getAgreements, generateAgreements],
  );

  const fetchAgreementContradictions = useCallback(
    async (signal?: AbortSignal) => {
      setError(null);
      setAgreementContradictionsHasFetched(false);

      if (transcripts.length === 0) {
        setAgreementContradictionsHasFetched(true);
        return [];
      }

      try {
        const params = await buildParams();
        const mutation = generateContradictions(params);
        const abortHandler = () => mutation.abort();
        signal?.addEventListener('abort', abortHandler, { once: true });
        try {
          await mutation.unwrap();
        } finally {
          signal?.removeEventListener('abort', abortHandler);
        }

        if (signal?.aborted) return [];

        const newContradictions = await getContradictions(params).unwrap();
        if (!signal?.aborted) setAgreementContradictions(newContradictions);
        return newContradictions;
      } catch (err) {
        if (!signal?.aborted) {
          console.error('Error with agreement contradictions:', err);
          setError(err instanceof Error ? err.message : 'Failed to fetch agreement contradictions');
        }
        return [];
      } finally {
        if (!signal?.aborted) setAgreementContradictionsHasFetched(true);
      }
    },
    [transcripts.length, buildParams, getContradictions, generateContradictions],
  );

  const resolveDocumentPackageId = useCallback(
    (packageId?: string) =>
      packageId || selectedDocumentPackageId || meeting.documentPackageId || projectPackages[0]?.id,
    [selectedDocumentPackageId, meeting.documentPackageId, projectPackages],
  );

  const reportDocumentContradictionsError = useCallback((err: unknown) => {
    console.error('Error with document contradictions:', err);

    const taskError = readTaskError(err);
    if (taskError) {
      setDocumentContradictionsRetriable(taskError.retriable);
      setError(taskError.message);
      return;
    }

    const errorData = err as {
      status?: number;
      data?: { detail?: string };
      message?: string;
    };
    const status = errorData?.status;

    if (status === HTTP_STATUS.NOT_FOUND) {
      // Nothing has been analysed for this package yet — an empty list, not a failure.
      setDocumentContradictions([]);
    } else if (status === HTTP_STATUS.BAD_GATEWAY || status === HTTP_STATUS.SERVICE_UNAVAILABLE) {
      setError('Unable to reach the document service. Please try again.');
    } else if (status === HTTP_STATUS.BAD_REQUEST) {
      setError(errorData?.data?.detail || 'Invalid request. Please check the document package.');
    } else {
      setError(err instanceof Error ? err.message : 'Failed to fetch document contradictions');
    }
  }, []);

  const fetchDocumentContradictions = useCallback(
    async (packageId?: string, signal?: AbortSignal) => {
      setError(null);
      setDocumentContradictionsHasFetched(false);
      setDocumentContradictionsRetriable(true);
      setDocumentContradictions([]);

      const documentPackageId = resolveDocumentPackageId(packageId);

      if (!documentPackageId) {
        setError(
          'No document package available for this project. Please upload documents to analyze contradictions.',
        );
        setDocumentContradictionsRetriable(false);
        setDocumentContradictionsHasFetched(true);
        return [];
      }

      try {
        const params = await buildParams();
        const existing = await getDocumentContradictions({
          ...params,
          documentPackageId,
        }).unwrap();

        if (!signal?.aborted && existing.length === 0) {
          // Nothing analysed yet — kick off generation automatically.
          const mutation = generateDocumentContradictions({ ...params, documentPackageId });
          const abortHandler = () => mutation.abort();
          signal?.addEventListener('abort', abortHandler, { once: true });
          try {
            await mutation.unwrap();
          } finally {
            signal?.removeEventListener('abort', abortHandler);
          }

          if (signal?.aborted) return [];

          const generated = await getDocumentContradictions({
            ...params,
            documentPackageId,
          }).unwrap();
          if (!signal?.aborted) setDocumentContradictions(generated);
          return generated;
        }

        if (!signal?.aborted) setDocumentContradictions(existing);
        return existing;
      } catch (err) {
        if (!signal?.aborted) reportDocumentContradictionsError(err);
        return [];
      } finally {
        if (!signal?.aborted) setDocumentContradictionsHasFetched(true);
      }
    },
    [
      resolveDocumentPackageId,
      buildParams,
      getDocumentContradictions,
      generateDocumentContradictions,
      reportDocumentContradictionsError,
    ],
  );

  const handleDocumentPackageChange = useCallback(
    (packageId: string) => {
      setSelectedDocumentPackageId(packageId);
      if (documentPackageStorageKey) {
        try {
          localStorage.setItem(documentPackageStorageKey, packageId);
        } catch {}
      }
      fetchDocumentContradictions(packageId);
    },
    [fetchDocumentContradictions, documentPackageStorageKey],
  );

  const fetchAllInFlightRef = useRef<Promise<{
    summary: MeetingSummaryType | null;
    actionItems: ActionItem[];
    agreements: MeetingAgreement[];
  }> | null>(null);

  const fetchAllData = useCallback(async () => {
    if (fetchAllInFlightRef.current) {
      return fetchAllInFlightRef.current;
    }

    const run = (async () => {
      const [fetchedSummary, fetchedActionItems, fetchedAgreements] = await Promise.all([
        summary ? Promise.resolve(summary) : fetchSummary(),
        actionItems.length > 0 ? Promise.resolve(actionItems) : fetchActionItems(),
        agreements.length > 0 ? Promise.resolve(agreements) : fetchAgreements(),
      ]);

      return {
        summary: fetchedSummary ?? null,
        actionItems: fetchedActionItems ?? [],
        agreements: fetchedAgreements ?? [],
      };
    })();

    fetchAllInFlightRef.current = run;
    try {
      return await run;
    } finally {
      fetchAllInFlightRef.current = null;
    }
  }, [summary, actionItems, agreements, fetchSummary, fetchActionItems, fetchAgreements]);

  const assignSpeaker = useCallback(
    async (entry: ParsedTranscriptEntry, meetingUserId: string, scope: SpeakerAssignScope) => {
      if (!entry.segmentId) return;

      const params = await buildParams();
      await assignSegmentSpeaker({
        ...params,
        segmentId: entry.segmentId,
        body: { meetingUserId, scope },
      }).unwrap();

      const chosen = participants.find((p) => p.id === meetingUserId);
      const chosenName = chosen?.name || chosen?.email || entry.speaker;

      const matchesBatch = (e: ParsedTranscriptEntry): boolean => {
        if (scope === 'all_same_name' && entry.speakerName != null) {
          return e.speakerName === entry.speakerName;
        }
        if (scope === 'all_same_user' && entry.assignedMeetingUserId != null) {
          return e.assignedMeetingUserId === entry.assignedMeetingUserId;
        }
        return e.segmentId === entry.segmentId;
      };

      setParsedTranscripts((prev) =>
        prev.map((e) => {
          if (!matchesBatch(e)) return e;
          return {
            ...e,
            speaker: chosenName,
            speakerName: null,
            assignedMeetingUserId: meetingUserId,
          };
        }),
      );

      renameVersionRef.current += 1;
      setActionItems([]);
      setAgreements([]);
    },
    [buildParams, assignSegmentSpeaker, participants],
  );

  return {
    transcripts,
    parsedTranscripts,
    actionItems,
    agreements,
    agreementContradictions,
    agreementContradictionsHasFetched,
    documentContradictions,
    documentContradictionsHasFetched,
    documentContradictionsRetriable,
    summary,
    error,
    errorType,
    transcriptInfoMessage,
    canRegenerateWithAudio,
    audioUnavailable,
    summaryErrorType,
    actionItemsError,
    selectedDocumentPackageId,
    projectPackages,
    participants,
    assignSpeaker,
    isTranscriptLoading: isLoadingTranscripts || isFetchingTranscripts,
    isSummaryLoading: isLoadingSummary || isCreatingSummary,
    isSummaryGenerating: isCreatingSummary,
    isAgreementsLoading: isLoadingAgreements || isRefetchingAgreements,
    isAgreementsGenerating: isGeneratingAgreements,
    isLoadingActionItems: isLoadingActionItems || isRefetchingActionItems,
    isGeneratingActionItems,
    isLoadingContradictions,
    isGeneratingContradictions,
    // isFetching too: a package already in the RTK cache re-reads without
    // isLoading, and the tab would otherwise sit blank during that read.
    isLoadingDocumentContradictions:
      isLoadingDocumentContradictions || isFetchingDocumentContradictions,
    isGeneratingDocumentContradictions,
    setSummary,
    setActionItems,
    setAgreements,
    setAgreementContradictions,
    setDocumentContradictions,
    setError,
    buildParams,
    fetchTranscripts,
    applyTranscripts,
    resetGeneratedData,
    fetchSummary,
    saveSummary,
    fetchActionItems,
    fetchAgreements,
    fetchAgreementContradictions,
    fetchDocumentContradictions,
    handleDocumentPackageChange,
    fetchAllData,
  };
};
